import React from 'react';
import { useApolloClient } from '@apollo/client';

import { requestLaneEnrichment, rerunLane as rerunStuckLane, type LaneRerunReason } from '@hopdrive/sdk-lanes';
import { laneLifecycleStatus, useLaneLifecycle, type LaneLifecycleSubscription } from '@hopdrive/feature-lane-lifecycle';
import { useUser } from '@store';
import { Lanes } from '@gql/base-types';
import {
  GetLaneLifecycleDocument,
  type GetLaneLifecycleSubscription,
} from '../queries/getLaneLifecycle.subscription.generated';
import { laneAfterRerun } from '../utils/laneEnrichment';

export const useLaneLifecycleSubscription: LaneLifecycleSubscription<Lanes> = ({ laneId, skip, generation }) => {
  const client = useApolloClient();
  const [observation, setObservation] = React.useState<{
    generation: string;
    lane?: Lanes | null;
    error?: unknown;
  }>({ generation });

  React.useEffect(() => {
    setObservation({ generation });
    if (skip || !laneId) return;

    let active = true;
    const subscription = client
      .subscribe<GetLaneLifecycleSubscription>({
        query: GetLaneLifecycleDocument,
        variables: { laneId },
        errorPolicy: 'all',
      })
      .subscribe({
        next: ({ data }) => {
          if (active) setObservation({ generation, lane: data?.lanes_by_pk as Lanes | null | undefined });
        },
        error: error => {
          if (active) setObservation({ generation, error });
        },
      });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [client, generation, laneId, skip]);

  return observation;
};

/**
 * Follow one lane shell until it becomes ready or reaches a failed/stale terminal
 * state. A lane that is not moving is rerun through sdk-lanes `rerunLane` for the
 * reason the lifecycle hook decided; the result carries the hook's `stalled`,
 * `gaveUp`, and `error` to the planner.
 */
export function useLaneEnrichment(
  lane: Lanes | null | undefined,
  generation: string,
  onUpdate: (updated: Lanes) => void
) {
  const user = useUser();
  const actor = React.useMemo(
    () => ({
      user: user?.claims?.['x-hasura-user-email'] || 'dealer',
      role: user?.claims?.['x-hasura-default-role'] || 'dealer',
    }),
    [user]
  );

  const retryLane = React.useCallback(
    async (laneId: number): Promise<Lanes> => {
      const result = await requestLaneEnrichment({
        laneId,
        ...actor,
        customerId: lane?.customer_id ?? undefined,
      });
      return { ...lane, id: result.laneId, status: result.status } as Lanes;
    },
    [actor, lane]
  );

  const rerunLane = React.useCallback(
    async (target: Lanes, reason: LaneRerunReason): Promise<Lanes | null> => {
      const result = await rerunStuckLane({
        lane: { ...target, status: laneLifecycleStatus(target) },
        reason,
        ...actor,
        customerId: target.customer_id ?? undefined,
      });
      return laneAfterRerun(target, result);
    },
    [actor]
  );

  return useLaneLifecycle({
    lane,
    generation,
    useSubscription: useLaneLifecycleSubscription,
    retryLane,
    rerunLane,
    onUpdate,
  });
}
